import React from "react";
import { Box } from "@mui/material";
import { replaceTemplateTagsForPreview } from "Util/notificationTemplatePreview";
import useStyles from "./styles";
import { CloseIcon, sharedNotificationStyles } from "./sharedStyles";

interface PushNotificationPreviewProps {
  title?: string;
  content?: string;
  appName?: string;
  className?: string;
  showRawTags?: boolean;
  viewMode?: "mobile" | "web";
}

const PushNotificationPreview: React.FC<PushNotificationPreviewProps> = ({
  title = "",
  content = "",
  appName = "Shipeezi",
  className,
  showRawTags = false,
  viewMode = "mobile",
}) => {
  const classes = useStyles();

  const previewTitle = React.useMemo(() => {
    if (!title) return "";
    return replaceTemplateTagsForPreview(title, showRawTags, false);
  }, [title, showRawTags]);

  const previewContent = React.useMemo(() => {
    if (!content) return "";
    return replaceTemplateTagsForPreview(content, showRawTags, false);
  }, [content, showRawTags]);

  const currentTime = React.useMemo(() => {
    const now = new Date();
    return now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
    });
  }, []);

  const notificationTimestamp = React.useMemo(() => {
    const now = new Date();
    return now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }, []);

  const hasContent = previewTitle || previewContent;

  // Mobile
  if (viewMode === "mobile") {
    return (
      <Box className={`${classes.phoneContainer} ${className || ""}`}>
        <Box className={classes.phoneFrame}>
          <Box className={classes.phoneScreen}>
            <Box className={classes.statusBar}>
              <Box className={classes.statusBarLeft}>{currentTime}</Box>
              <Box className={classes.dynamicIsland} />
              <Box className={classes.statusBarRight}>
                <Box className={classes.statusBarIcon}>
                  <svg width="15" height="10" viewBox="0 0 15 10" fill="none">
                    <path
                      d="M1 9L3 7M5 9L7 5M9 9L11 3M13 9L15 1"
                      stroke="#000"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </Box>
                <Box className={classes.statusBarIcon}>
                  <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                    <path
                      d="M7 9C7.55228 9 8 8.55228 8 8C8 7.44772 7.55228 7 7 7C6.44772 7 6 7.44772 6 8C6 8.55228 6.44772 9 7 9Z"
                      fill="#000"
                    />
                    <path
                      d="M3.5 5.5C4.88071 4.11929 6.88071 4.11929 8.26143 5.5"
                      stroke="#000"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M1 3C3.68629 0.313708 7.31371 0.313708 10 3"
                      stroke="#000"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                  </svg>
                </Box>
                <Box className={classes.statusBarIcon}>
                  <svg width="23" height="11" viewBox="0 0 23 11" fill="none">
                    <rect x="1" y="2" width="19" height="7" rx="1" stroke="#000" strokeWidth="1.2" />
                    <rect x="20" y="4" width="1.5" height="3" fill="#000" />
                    <rect x="2.5" y="3.5" width="15" height="5" rx="0.5" fill="#000" />
                  </svg>
                </Box>
              </Box>
            </Box>

            {/* Push Notification Banner */}
            {hasContent ? (
              <Box className={classes.notificationBanner}>
                <Box className={classes.notificationHeader}>
                  <Box className={classes.notificationIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="8" r="4" fill="#98A2B3" />
                      <path
                        d="M6 20C6 16 9 14 12 14C15 14 18 16 18 20"
                        stroke="#98A2B3"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </Box>
                  <Box className={classes.notificationHeaderContent}>
                    <Box component="span" className={classes.notificationAppName}>
                      {appName}
                    </Box>
                    <Box component="span" className={classes.notificationTimestamp}>
                      {notificationTimestamp}
                    </Box>
                  </Box>
                </Box>
                {previewTitle && (
                  <Box component="div" className={classes.notificationTitle}>
                    {previewTitle}
                  </Box>
                )}
                {previewContent && (
                  <Box component="div" className={classes.notificationBody}>
                    {previewContent}
                  </Box>
                )}
              </Box>
            ) : (
              <Box className={classes.emptyState}>
                Start typing to see your push notification preview...
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  const metaLine = `${appName} · ${notificationTimestamp}`;

  return (
    <Box className={`${classes.webContainer} ${className || ""}`}>
      {hasContent ? (
        <Box style={sharedNotificationStyles.pushNotificationCard}>
          <Box style={sharedNotificationStyles.pushNotificationCardContent}>
            <Box style={sharedNotificationStyles.pushNotificationCardText}>
              <Box style={sharedNotificationStyles.pushNotificationCardMeta}>{metaLine}</Box>
              <Box style={sharedNotificationStyles.pushNotificationCardTitle}>
                {previewTitle}
              </Box>
              {previewContent && (
                <Box style={sharedNotificationStyles.pushNotificationCardBody}>
                  {previewContent}
                </Box>
              )}
            </Box>
            <CloseIcon style={sharedNotificationStyles.pushNotificationCardCloseButton} />
          </Box>
        </Box>
      ) : (
        <Box style={sharedNotificationStyles.pushNotificationCard}>
          <Box style={sharedNotificationStyles.pushNotificationCardEmptyState}>
            Start typing to see your push notification preview...
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PushNotificationPreview;

